import React, {useCallback, useEffect, useRef, useState} from 'react';
import {MapContainer, Marker, TileLayer, CircleMarker, useMap} from 'react-leaflet';
import L, {Icon, LatLngExpression} from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet-gpx'
import 'leaflet/dist/leaflet.css';
import './Map.css';
import { LayerSelector, type LayerOption } from '../Controls';
import { AuthButton, AuthModal } from '../Auth';
import { ZoomControl, LocationControl } from '../Controls';
import { ToolsButton, ToolsPanel, EditTraceModal, SaveTraceModal } from '../Tools';
import { DrawingControl, DrawingButton } from '../Drawing';
import {useAuth} from '../../hooks/useAuth';
import {useGPXTraces} from '../../hooks/useGPXTraces';
import {GPXTrace, ElevationPoint, gpxTraceService} from '../../services/gpxTraceService';
import ElevationChart from '../GPX/ElevationChart';

delete (L.Icon.Default.prototype as unknown as { _getIconUrl: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Fix for default Leaflet icon
const defaultIcon = new Icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

// Icônes explicites pour leaflet-gpx (évite le bug webpack qui casse L.Icon.Default)
const gpxMarkerOptions = {
    markers: {
        startIcon: new Icon({ iconUrl: markerIcon, shadowUrl: markerShadow, iconSize: [25, 41], iconAnchor: [12, 41] }),
        endIcon:   new Icon({ iconUrl: markerIcon, shadowUrl: markerShadow, iconSize: [25, 41], iconAnchor: [12, 41] }),
        wptIcons:  { '': new Icon({ iconUrl: markerIcon, shadowUrl: markerShadow, iconSize: [25, 41], iconAnchor: [12, 41] }) },
    },
    polyline_options: {
        color: '#1a6fdb',   // bleu vif
        weight: 4,
        opacity: 0.95,
    },
};

// Calcule la distance en km entre deux points (formule haversine)
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Aplatit un tableau potentiellement imbriqué de LatLng
function flattenLatLngs(arr: L.LatLng | L.LatLng[] | L.LatLng[][]): L.LatLng[] {
    if (Array.isArray(arr)) {
        return (arr as (L.LatLng | L.LatLng[])[]).flatMap(flattenLatLngs);
    }
    return [arr as L.LatLng];
}

// Ajoute des marqueurs de distance tous les INTERVAL_KM kilomètres sur la trace
function addDistanceMarkers(gpx: L.GPX, map: L.Map, intervalKm = 5): L.Marker[] {
    const markers: L.Marker[] = [];
    const allPoints: L.LatLng[] = [];

    function walk(layer: L.Layer): void {
        if ((layer as L.LayerGroup).getLayers) {
            (layer as L.LayerGroup).getLayers().forEach(walk);
        }
        if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
            allPoints.push(...flattenLatLngs(layer.getLatLngs() as L.LatLng[]));
        }
    }
    walk(gpx);

    if (allPoints.length < 2) return markers;

    let cumulDist = 0;
    let nextMilestone = intervalKm;

    for (let i = 1; i < allPoints.length; i++) {
        const prev = allPoints[i - 1];
        const curr = allPoints[i];
        const segDist = haversineKm(prev.lat, prev.lng, curr.lat, curr.lng);
        cumulDist += segDist;

        while (cumulDist >= nextMilestone) {
            const excess = cumulDist - nextMilestone;
            const ratio = segDist > 0 ? excess / segDist : 0;
            const lat = curr.lat + ratio * (prev.lat - curr.lat);
            const lng = curr.lng + ratio * (prev.lng - curr.lng);

            const icon = L.divIcon({
                html: `<div class="distance-marker">${nextMilestone} km</div>`,
                className: '',
                iconSize: [44, 20],
                iconAnchor: [22, 10],
            });
            const marker = L.marker([lat, lng], { icon, interactive: false, zIndexOffset: -100 });
            marker.addTo(map);
            markers.push(marker);
            nextMilestone += intervalKm;
        }
    }
    return markers;
}

// Ajoute une ligne de bordure blanche sous la polyline GPX pour la rendre bien lisible
// sur toutes les fonds de carte (effet "casing" comme sur les cartes de navigation).
// leaflet-gpx neste les polylines dans des FeatureGroups (un par <trk>) →
// on parcourt l'arbre récursivement pour trouver toutes les L.Polyline.
function addGpxBorderLine(gpx: L.GPX, map: L.Map): L.Polyline[] {
    const borderLines: L.Polyline[] = [];
    function walk(layer: L.Layer): void {
        if ((layer as L.LayerGroup).getLayers) {
            (layer as L.LayerGroup).getLayers().forEach(walk);
        }
        if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
            const borderLine = L.polyline(layer.getLatLngs() as L.LatLng[] | L.LatLng[][], {
                color: 'white',
                weight: 10,
                opacity: 0.7,
                interactive: false,
            }).addTo(map);
            borderLine.bringToBack();
            borderLines.push(borderLine);
        }
    }
    walk(gpx);
    return borderLines;
}

// Custom icon for user's current location
const userLocationIcon = L.divIcon({
    html: '<div class="user-location-marker"></div>',
    className: 'user-location-icon',
    iconSize: [30, 30],
    iconAnchor: [15, 15]
});

// Component to update map view based on location
const SetViewOnLocation = ({coords}: { coords: LatLngExpression }) => {
    const map = useMap();
    useEffect(() => {
        if (coords) {
            map.setView(coords, 13);
        }
    }, [coords, map]);
    return null;
};

interface MapProps {
    traceGpxFile?: string;
    mapHeight?: string;
    profileHoveredPosition?: { lat: number; lon: number } | null;
}

interface GPXLoadEvent extends L.LeafletEvent {
    target: { getBounds: () => L.LatLngBounds };
}


const Map: React.FC<MapProps> = ({traceGpxFile, mapHeight = '100vh', profileHoveredPosition}) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const tilesUrl = process.env.REACT_APP_TILES_URL ?? '';
    // Default position (Paris)
    const defaultPosition: LatLngExpression = [48.845, 2.424];
    const savedPosition = (() => {
        try {
            const raw = localStorage.getItem('geotrail_last_position');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length === 2) return parsed as LatLngExpression;
            }
        } catch { /* ignore */ }
        return null;
    })();
    const [position, setPosition] = useState<LatLngExpression>(savedPosition ?? defaultPosition);
    const [userPosition, setUserPosition] = useState<LatLngExpression | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [geoError, setGeoError] = useState<string | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const hasRestoredTracesRef = useRef(false);
    const [mapReady, setMapReady] = useState(false);
    const [gpxLoaded, setGpxLoaded] = useState<boolean>(false);
    const [gpxLoading, setGpxLoading] = useState(false);
    const [selectedLayer, setSelectedLayer] = useState<string>('ign');
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [toolsPanelOpen, setToolsPanelOpen] = useState(false);
    const [selectedTraces, setSelectedTraces] = useState<GPXTrace[]>([]);
    const [loadedGPXLayers, setLoadedGPXLayers] = useState<{ [key: string]: L.GPX }>({});
    const [loadedBorderLines, setLoadedBorderLines] = useState<{ [key: string]: L.Polyline[] }>({});
    const [loadedDistanceMarkers, setLoadedDistanceMarkers] = useState<{ [key: string]: L.Marker[] }>({});
    const [loadedTracePaths, setLoadedTracePaths] = useState<Set<string>>(new Set());
    const [drawingMode, setDrawingMode] = useState<'walking' | 'cycling' | 'driving' | null>(null);
    const [routingProvider, setRoutingProvider] = useState<'osrm' | 'ign'>('osrm');
    const [editingTrace, setEditingTrace] = useState<GPXTrace | null>(null);
    const [pendingTrackCoords, setPendingTrackCoords] = useState<L.LatLng[] | null>(null);
    const [saveTraceError, setSaveTraceError] = useState<string | null>(null);
    const [isSavingTrace, setIsSavingTrace] = useState(false);
    const [editingGeometryTrace, setEditingGeometryTrace] = useState<GPXTrace | null>(null);
    const [editingInitialWaypoints, setEditingInitialWaypoints] = useState<L.LatLng[] | null>(null);
    const [showElevationPanel, setShowElevationPanel] = useState(false);
    const [elevationProfile, setElevationProfile] = useState<ElevationPoint[] | null>(null);
    const [elevationProfilePath, setElevationProfilePath] = useState<string | null>(null);
    const [hoveredMapPosition, setHoveredMapPosition] = useState<{ lat: number; lon: number } | null>(null);

    // Pan vers la position survolée sur le profil si elle n'est pas visible dans la carte
    useEffect(() => {
        const pos = profileHoveredPosition ?? hoveredMapPosition;
        if (!pos || !mapRef.current) return;
        const map = mapRef.current;
        const latlng = L.latLng(pos.lat, pos.lon);
        if (!map.getBounds().contains(latlng)) {
            map.panTo(latlng, { animate: true, duration: 0.4 });
        }
    }, [profileHoveredPosition, hoveredMapPosition]);

    // Utilisation du hook d'authentification
    const {user, isAuthenticated, isLoading: authLoading, error: authError, message: authMessage, login, register, logout, clearMessage} = useAuth();

    // Utilisation du hook pour gérer les traces GPX
    const {
        traces,
        isLoading: isLoadingTraces,
        hasMore: hasMoreTraces,
        totalTraces,
        refreshTraces: _refreshTraces,
        loadMoreTraces,
        uploadTrace: uploadTraceService,
        deleteTrace: deleteTraceService
    } = useGPXTraces();

    // Configuration des couches disponibles
    const layers: LayerOption[] = [
        {
            id: 'ign',
            name: 'IGN Geoportail',
            icon: '🗺️',
            url: `${tilesUrl}/ign/{z}/{x}/{y}.png`,
            attribution: 'IGN-F/Geoportail',
            minZoom: 6,
            maxZoom: 16,
            tileSize: 256
        },
        {
            id: 'osm',
            name: 'OpenStreetMap',
            icon: '🌍',
            url: `${tilesUrl}/osm/{z}/{x}/{y}.png`,
            attribution: 'OpenStreetMap',
            maxZoom: 19
        },
        {
            id: 'otm',
            name: 'OpenTopoMap',
            icon: '🏔️',
            url: `${tilesUrl}/otm/{z}/{x}/{y}.png`,
            attribution: 'OpenTopoMap',
            maxZoom: 17
        }
    ];

    const currentLayer = layers.find(layer => layer.id === selectedLayer) || layers[0];

    // Fonction pour charger une trace GPX sur la carte
    const loadTraceOnMap = useCallback((trace: GPXTrace, forceUrl?: string) => {
        if (!mapRef.current) return;
        // Si pas de rechargement forcé, vérifier la déduplication
        if (!forceUrl && loadedGPXLayers[trace.path]) {
            console.log('Trace already loaded:', trace.original_name);
            return;
        }
        // Si rechargement forcé avec une ancienne couche présente, la retirer d'abord
        if (forceUrl && loadedGPXLayers[trace.path]) {
            mapRef.current.removeLayer(loadedGPXLayers[trace.path]);
        }

        setGpxLoading(true);
        const gpxUrl = forceUrl ?? gpxTraceService.getTraceGPXUrl(trace.path, trace.updated_at);
        const gpx = new L.GPX(gpxUrl, {async: true, ...gpxMarkerOptions});

        gpx.on('loaded', function (e: GPXLoadEvent) {
            if (mapRef.current) {
                const borderLines = addGpxBorderLine(gpx, mapRef.current);
                setLoadedBorderLines(prev => ({...prev, [trace.path]: borderLines}));

                const distMarkers = addDistanceMarkers(gpx, mapRef.current);
                setLoadedDistanceMarkers(prev => ({...prev, [trace.path]: distMarkers}));

                // Stocker la couche GPX pour cette trace
                setLoadedGPXLayers(prev => ({...prev, [trace.path]: gpx}));
                setLoadedTracePaths(prev => new Set(prev).add(trace.path));

                // Ajuster la vue pour inclure toutes les traces si c'est la première
                if (Object.keys(loadedGPXLayers).length === 0) {
                    mapRef.current.fitBounds(e.target.getBounds());
                }

                setGpxLoaded(true);
                setGpxLoading(false);
                console.log('GPX trace loaded successfully:', trace.original_name);
            }
        });

        gpx.on('error', function (e: L.LeafletEvent) {
            console.error('Error loading GPX:', e);
            setGpxLoading(false);
            alert('Erreur lors du chargement de la trace GPX');
        });

        gpx.addTo(mapRef.current);
    }, [loadedGPXLayers]); // Dépendance nécessaire pour vérifier les traces déjà chargées

    // Sauvegarder les IDs des traces sélectionnées dans localStorage (seulement après restauration)
    useEffect(() => {
        if (!hasRestoredTracesRef.current) return;
        const ids = selectedTraces.map(t => t.id);
        localStorage.setItem('geotrail_selected_trace_ids', JSON.stringify(ids));
    }, [selectedTraces]);

    // Recharger les traces quand l'utilisateur se connecte
    useEffect(() => {
        if (isAuthenticated) {
            _refreshTraces();
        }
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // Restaurer les traces sélectionnées depuis localStorage une fois les traces chargées
    useEffect(() => {
        if (!mapReady) return;
        // Si pas authentifié, on n'a rien à restaurer — débloquer les sauvegardes directement
        if (!isAuthenticated) {
            hasRestoredTracesRef.current = true;
            return;
        }
        if (isLoadingTraces || traces.length === 0) return;
        try {
            const raw = localStorage.getItem('geotrail_selected_trace_ids');
            if (!raw) return;
            const savedIds: number[] = JSON.parse(raw);
            if (!Array.isArray(savedIds) || savedIds.length === 0) return;
            savedIds.forEach(id => {
                const trace = traces.find(t => t.id === id);
                if (!trace) return;
                if (selectedTraces.find(t => t.id === id)) return; // déjà sélectionnée
                setSelectedTraces(prev => [...prev, trace]);
                loadTraceOnMap(trace);
            });
        } catch { /* ignore */ } finally {
            hasRestoredTracesRef.current = true;
        }
    }, [mapReady, isAuthenticated, isLoadingTraces, traces]); // eslint-disable-line react-hooks/exhaustive-deps

    // Chargement de la trace GPX si besoin (mode legacy pour traceGpxFile)
    useEffect(() => {
        setGpxLoaded(false);
        setGpxLoading(false);
        if (traceGpxFile && mapRef.current && mapReady) {
            setGpxLoading(true);
            const gpxUrl = `${apiUrl}/gpx/${traceGpxFile}`;
            const gpx = new L.GPX(gpxUrl, {async: true, ...gpxMarkerOptions});
            gpx.on('loaded', function (e: GPXLoadEvent) {
                if (mapRef.current) {
                    addGpxBorderLine(gpx, mapRef.current);
                    addDistanceMarkers(gpx, mapRef.current);
                }
                mapRef.current?.fitBounds(e.target.getBounds());
                setGpxLoaded(true);
                setGpxLoading(false);
            });
            gpx.addTo(mapRef.current);
        }
    }, [traceGpxFile, apiUrl, mapReady]);

    // Géolocalisation utilisateur
    useEffect(() => {
        if (gpxLoaded) {
            // Trace GPX chargée → pas besoin de géolocalisation
            console.log(`GPX file is loaded, skipping geolocation`);
            setLoading(false);
        } else if (!traceGpxFile) {
            // Mode carte standard (pas de trace partagée) → on tente la géolocalisation
            if (navigator.geolocation) {
                console.log("Attempting to get user's geolocation...");
                setLoading(true);
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        const {latitude, longitude} = pos.coords;
                        const userPos: LatLngExpression = [latitude, longitude];
                        localStorage.setItem('geotrail_last_position', JSON.stringify(userPos));
                        setPosition(userPos);
                        setUserPosition(userPos);
                        setLoading(false);
                    },
                    (err) => {
                        console.error("Geolocation error:", err);
                        setGeoError("Unable to access your position");
                        setLoading(false);
                    },
                    {enableHighAccuracy: true, timeout: 5000, maximumAge: 0}
                );
            } else {
                setGeoError("Geolocation is not supported by your browser");
                setLoading(false);
            }
        }
        // Si traceGpxFile est défini mais pas encore chargé, on attend silencieusement
        // (le loading est géré par l'effet de chargement GPX ci-dessus)
    }, [gpxLoaded, traceGpxFile]);



    // Fonction pour accéder à l'instance de la carte
    const getMapInstance = (map: L.Map) => {
        mapRef.current = map;
    };

    // Fonctions d'authentification
    const handleOpenAuthModal = () => {
        setAuthModalOpen(true);
    };

    const handleLogout = () => {
        logout();
    };

    const handleAuthModalClose = () => {
        setAuthModalOpen(false);
    };

    // Fonctions du panel d'outils
    const handleToggleToolsPanel = () => {
        setToolsPanelOpen(!toolsPanelOpen);
    };

    const handleCloseToolsPanel = () => {
        setToolsPanelOpen(false);
    };

    const handleUploadTrace = async (file: File) => {
        try {
            await uploadTraceService(file);
            console.log('Trace uploaded successfully:', file.name);
        } catch (error) {
            console.error('Error uploading trace:', error);
        }
    };

    const handleCreateTrace = (mode: 'walking' | 'cycling' | 'driving', provider: 'osrm' | 'ign') => {
        setDrawingMode(mode);
        setRoutingProvider(provider);
        setToolsPanelOpen(false);
    };

    const handleTrackCreated = useCallback((latlngs: L.LatLng[]) => {
        setPendingTrackCoords(latlngs);
        setSaveTraceError(null);
    }, []);

    const handleSaveTraceConfirm = async (name: string, description: string) => {
        if (!pendingTrackCoords) return;
        setIsSavingTrace(true);
        setSaveTraceError(null);
        try {
            const savedTrace = await gpxTraceService.saveRouteAsTrace(
                name,
                pendingTrackCoords.map(p => [p.lat, p.lng]),
                description || undefined
            );
            setPendingTrackCoords(null);
            _refreshTraces();
            // Afficher immédiatement la trace sauvegardée sur la carte
            setSelectedTraces(prev => [...prev, savedTrace]);
            loadTraceOnMap(savedTrace);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de la trace:', error);
            setSaveTraceError('Erreur lors de la sauvegarde de la trace.');
        } finally {
            setIsSavingTrace(false);
        }
    };

    const handleSaveTraceCancel = () => {
        setPendingTrackCoords(null);
        setSaveTraceError(null);
    };
    const handleSelectTrace = (traceId: number) => {
        const trace = traces.find(t => t.id === traceId);
        if (!trace) return;

        // Vérifier si la trace est déjà sélectionnée
        if (selectedTraces.find(t => t.id === traceId)) {
            console.log('Trace already selected:', trace.original_name);
            return;
        }

        // Ajouter la trace à la liste des traces sélectionnées
        setSelectedTraces(prev => [...prev, trace]);

        // Charger la trace sur la carte si elle n'est pas déjà chargée
        if (mapRef.current && mapReady && !loadedTracePaths.has(trace.path)) {
            loadTraceOnMap(trace);
        }

        console.log('Selected trace:', trace.original_name);
    };

    const handleDeselectTrace = (traceId?: number) => {
        if (traceId) {
            // Déselectionner une trace spécifique
            const trace = selectedTraces.find(t => t.id === traceId);
            if (!trace) return;

            setSelectedTraces(prev => prev.filter(t => t.id !== traceId));

            // Supprimer la trace de la carte
            const gpxLayer = loadedGPXLayers[trace.path];
            if (gpxLayer && mapRef.current) {
                mapRef.current.removeLayer(gpxLayer);
                const borders = loadedBorderLines[trace.path];
                if (borders) {
                    borders.forEach(line => mapRef.current && mapRef.current.removeLayer(line));
                    setLoadedBorderLines(prev => {
                        const newBorders = {...prev};
                        delete newBorders[trace.path];
                        return newBorders;
                    });
                }
                setLoadedGPXLayers(prev => {
                    const newLayers = {...prev};
                    delete newLayers[trace.path];
                    return newLayers;
                });
                const distMarkers = loadedDistanceMarkers[trace.path];
                if (distMarkers) {
                    distMarkers.forEach(m => mapRef.current && mapRef.current.removeLayer(m));
                    setLoadedDistanceMarkers(prev => {
                        const updated = {...prev};
                        delete updated[trace.path];
                        return updated;
                    });
                }
                setLoadedTracePaths(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(trace.path);
                    return newSet;
                });
            }
        } else {
            // Déselectionner toutes les traces
            setSelectedTraces([]);
            setShowElevationPanel(false);

            // Supprimer toutes les traces de la carte
            if (mapRef.current) {
                const map = mapRef.current;
                Object.values(loadedGPXLayers).forEach(gpxLayer => {
                    map.removeLayer(gpxLayer);
                });
                Object.values(loadedBorderLines).forEach(lines => {
                    lines.forEach(line => map.removeLayer(line));
                });
                Object.values(loadedDistanceMarkers).forEach(markers => {
                    markers.forEach(m => map.removeLayer(m));
                });
                setLoadedGPXLayers({});
                setLoadedBorderLines({});
                setLoadedDistanceMarkers({});
                setLoadedTracePaths(new Set());
                setGpxLoaded(false);
            }
        }
    };
    const handleDeleteTrace = async (traceId: number) => {
        try {
            // Trouver la trace pour obtenir son path
            const trace = traces.find(t => t.id === traceId);
            if (!trace) return;

            await deleteTraceService(trace.path);

            // Si la trace supprimée était sélectionnée, la déselectionner
            if (selectedTraces.find(t => t.id === traceId)) {
                handleDeselectTrace(traceId);
            }

            console.log('Trace deleted successfully');
        } catch (error) {
            console.error('Error deleting trace:', error);
        }
    };

    const handleEditTrace = (trace: GPXTrace) => {
        setEditingTrace(trace);
    };

    const handleEditTraceSuccess = (updated: GPXTrace) => {
        setEditingTrace(null);
        _refreshTraces();
        // Update selectedTraces in-place so the map reflects the new name immediately
        setSelectedTraces(prev => prev.map(t => t.id === updated.id ? updated : t));
    };

    const handleEditTraceGeometry = async (trace: GPXTrace) => {
        try {
            // 1. Récupérer et parser le fichier GPX
            const gpxUrl = gpxTraceService.getTraceGPXUrl(trace.path);
            const res = await fetch(gpxUrl);
            const text = await res.text();
            const doc = new DOMParser().parseFromString(text, 'application/xml');
            const trkpts = Array.from(doc.querySelectorAll('trkpt'));
            const allPoints: L.LatLng[] = trkpts.map(pt =>
                L.latLng(parseFloat(pt.getAttribute('lat') ?? '0'), parseFloat(pt.getAttribute('lon') ?? '0'))
            );

            // 2. Sous-échantillonner pour obtenir ~15 waypoints représentatifs
            const MAX_WAYPOINTS = 15;
            let waypoints: L.LatLng[];
            if (allPoints.length <= MAX_WAYPOINTS) {
                waypoints = allPoints;
            } else {
                waypoints = [];
                const step = (allPoints.length - 1) / (MAX_WAYPOINTS - 1);
                for (let i = 0; i < MAX_WAYPOINTS; i++) {
                    waypoints.push(allPoints[Math.round(i * step)]);
                }
            }

            // 3. Retirer la trace de la carte (pour voir le tracé d'édition)
            handleDeselectTrace(trace.id);

            // 4. Entrer en mode édition de géométrie
            setEditingGeometryTrace(trace);
            setEditingInitialWaypoints(waypoints);
            setDrawingMode('walking');
            setToolsPanelOpen(false);
        } catch (error) {
            console.error('Erreur lors du chargement de la trace pour édition:', error);
            alert('Impossible de charger la trace pour édition.');
        }
    };

    const handleEditGeometryStopped = useCallback(async (latlngs: L.LatLng[]) => {
        if (!editingGeometryTrace) return;
        const trace = editingGeometryTrace;
        setEditingGeometryTrace(null);
        setEditingInitialWaypoints(null);
        try {
            const updatedTrace = await gpxTraceService.updateTraceCoordinates(
                trace.path,
                latlngs.map(p => [p.lat, p.lng])
            );
            // Recharger la trace sur la carte — l'URL est versionnée via updated_at
            const freshUrl = gpxTraceService.getTraceGPXUrl(updatedTrace.path, updatedTrace.updated_at);
            setSelectedTraces(prev => [...prev, updatedTrace]);
            loadTraceOnMap(updatedTrace, freshUrl);
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la trace:', error);
            alert('Erreur lors de la mise à jour de la trace.');
        }
    }, [editingGeometryTrace, loadTraceOnMap]);

    const handleExportTrace = async () => {
        if (selectedTraces.length === 0) {
            alert('Veuillez sélectionner au moins une trace à exporter.');
            return;
        }

        try {
            if (selectedTraces.length === 1) {
                // Export d'une seule trace
                const trace = selectedTraces[0];
                await gpxTraceService.downloadTrace(trace.path, trace.original_name);
            } else {
                // Export multiple : télécharger chaque trace individuellement
                for (const trace of selectedTraces) {
                    await gpxTraceService.downloadTrace(trace.path, trace.original_name);
                    // Petite pause entre les téléchargements pour éviter de surcharger le navigateur
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        } catch (error) {
            console.error('Erreur lors de l\'export:', error);
            alert('Erreur lors du téléchargement des traces GPX.');
        }
    };

    const handleExportSingleTrace = async (trace: GPXTrace) => {
        try {
            await gpxTraceService.downloadTrace(trace.path, trace.original_name);
        } catch (error) {
            console.error('Erreur lors de l\'export de la trace:', error);
            alert('Erreur lors du téléchargement de la trace GPX.');
        }
    };

    const handleShareTrace = (trace: GPXTrace) => {
        const shareUrl = `${window.location.origin}/gpx/${trace.path}`;
        navigator.clipboard.writeText(shareUrl).then(() => {
            alert(`Lien copié dans le presse-papier !\n\n${shareUrl}`);
        }).catch(() => {
            prompt('Copiez ce lien pour partager la trace :', shareUrl);
        });
    };

    const handleToggleElevationPanel = async () => {
        const trace = selectedTraces[0];
        if (!trace) return;
        if (!showElevationPanel) {
            if (elevationProfilePath !== trace.path) {
                setElevationProfile(null);
                setElevationProfilePath(trace.path);
                try {
                    const data = await gpxTraceService.getElevationProfile(trace.path);
                    setElevationProfile(data.profile);
                } catch (e) {
                    console.error(e);
                    setElevationProfile([]);
                }
            }
            setShowElevationPanel(true);
        } else {
            setShowElevationPanel(false);
        }
    };

    const handleLocationFound = (position: LatLngExpression) => {
        setUserPosition(position);
    };

    const profileTrace = selectedTraces[0] ?? null;
    const panelVisible = showElevationPanel && profileTrace !== null && !traceGpxFile;

    return (
        <div style={{position: 'relative', height: mapHeight}}>
            {loading && <div style={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 1000,
                background: 'white',
                padding: '5px 10px',
                borderRadius: '3px'
            }}>Getting your position...</div>}
            {geoError && <div style={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 1000,
                background: 'white',
                padding: '5px 10px',
                borderRadius: '3px'
            }}>{geoError}</div>}

            <LayerSelector
                layers={layers}
                selectedLayer={selectedLayer}
                onLayerChange={setSelectedLayer}
            />

            <AuthButton
                user={user}
                onOpenAuthModal={handleOpenAuthModal}
                onLogout={handleLogout}
            />

            {/* Bouton d'outils - visible seulement si l'utilisateur est connecté et pas en mode partage */}
            {isAuthenticated && !traceGpxFile && (
                <ToolsButton
                    onClick={handleToggleToolsPanel}
                    disabled={authLoading}
                />
            )}

            <MapContainer
                center={defaultPosition}
                zoom={10}
                style={{height: '100%', width: '100%'}}
                zoomControl={false}
                ref={getMapInstance}
                whenReady={() => setMapReady(true)}
            >
                <TileLayer
                    url={currentLayer.url}
                    attribution={currentLayer.attribution}
                    minZoom={currentLayer.minZoom}
                    maxZoom={currentLayer.maxZoom}
                    tileSize={currentLayer.tileSize}
                />

                {!traceGpxFile && !gpxLoading && !gpxLoaded && <SetViewOnLocation coords={position}/>}

                {/* Marqueur de survol du profil altimétrique */}
                {(() => {
                    const pos = profileHoveredPosition ?? hoveredMapPosition;
                    return pos ? (
                        <CircleMarker
                            center={[pos.lat, pos.lon]}
                            radius={8}
                            pathOptions={{ color: '#1976d2', weight: 2, fillColor: '#fff', fillOpacity: 1 }}
                        />
                    ) : null;
                })()}

                {/* Marqueur pour la position de l'utilisateur */}
                {userPosition && !loading && !geoError && (
                    <Marker
                        position={userPosition}
                        icon={userLocationIcon}
                        title="Votre position"
                    />
                )}

                {/* Marqueur par défaut si pas de position utilisateur */}
                {!userPosition && !loading && !geoError && !gpxLoaded && (
                    <Marker position={position} icon={defaultIcon}/>
                )}

                <ZoomControl/>
                <LocationControl onLocationFound={handleLocationFound}/>

                {drawingMode && (
                    <>
                        <DrawingControl
                            mode={drawingMode}
                            provider={routingProvider}
                            initialWaypoints={editingInitialWaypoints ?? undefined}
                            onTrackCreated={editingGeometryTrace ? handleEditGeometryStopped : handleTrackCreated}
                        />
                        <DrawingButton
                            mode={drawingMode}
                            label={editingGeometryTrace ? 'Terminer' : undefined}
                            onClick={() => setDrawingMode(null)}
                        />
                    </>
                )}
            </MapContainer>

            <AuthModal
                isOpen={authModalOpen}
                onClose={handleAuthModalClose}
                onLogin={login}
                onRegister={register}
                isLoading={authLoading}
                error={authError}
                message={authMessage}
                onClearMessage={clearMessage}
            />

            {/* Panel d'outils - visible seulement si l'utilisateur est connecté */}
            {isAuthenticated && (
                <ToolsPanel
                    isOpen={toolsPanelOpen}
                    onClose={handleCloseToolsPanel}
                    onUploadTrace={handleUploadTrace}
                    onCreateTrace={handleCreateTrace}
                    onSelectTrace={handleSelectTrace}
                    onDeselectTrace={handleDeselectTrace}
                    onEditTrace={handleEditTrace}
                    onEditTraceGeometry={handleEditTraceGeometry}
                    onExportTrace={handleExportTrace}
                    onExportSingleTrace={handleExportSingleTrace}
                    onShareTrace={handleShareTrace}
                    onDeleteTrace={handleDeleteTrace}
                    traces={traces}
                    selectedTraces={selectedTraces}
                    isLoadingTraces={isLoadingTraces}
                    hasMoreTraces={hasMoreTraces}
                    onLoadMoreTraces={loadMoreTraces}
                    totalTraces={totalTraces}
                />
            )}

            <EditTraceModal
                trace={editingTrace}
                onClose={() => setEditingTrace(null)}
                onSuccess={handleEditTraceSuccess}
            />

            <SaveTraceModal
                isOpen={pendingTrackCoords !== null}
                isSaving={isSavingTrace}
                error={saveTraceError}
                onConfirm={handleSaveTraceConfirm}
                onCancel={handleSaveTraceCancel}
            />

            {/* Bouton profil altimétrique */}
            {profileTrace && !traceGpxFile && (
                <button
                    className={`map-elevation-btn${showElevationPanel ? ' active' : ''}`}
                    onClick={handleToggleElevationPanel}
                    title={showElevationPanel ? 'Masquer le profil' : `Profil altimétrique — ${profileTrace.original_name}`}
                >
                    📈
                    {profileTrace.distance_km !== null && (
                        <span className="map-elevation-btn-dist">{profileTrace.distance_km.toFixed(1)} km</span>
                    )}
                </button>
            )}

            {/* Panneau profil altimétrique flottant */}
            {panelVisible && (
                <div className="map-elevation-panel">
                    <div className="map-elevation-panel-header">
                        <span className="map-elevation-panel-title">
                            {profileTrace.original_name}
                            {profileTrace.elevation_gain !== null && (
                                <>
                                    <span className="map-elev-stat up">↑ {Math.round(profileTrace.elevation_gain)} m</span>
                                    <span className="map-elev-stat down">↓ {Math.round(profileTrace.elevation_loss ?? 0)} m</span>
                                </>
                            )}
                        </span>
                        <button className="map-elevation-panel-close" onClick={() => setShowElevationPanel(false)}>✕</button>
                    </div>
                    <div className="map-elevation-panel-chart">
                        {elevationProfile === null
                            ? <p className="map-elevation-loading">Chargement…</p>
                            : <ElevationChart profile={elevationProfile} onHover={setHoveredMapPosition} />}
                    </div>
                </div>
            )}
        </div>
    );
};


export default Map;